import { useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentPreviewModal from 'components/document-preview-modal/document-preview-modal';
import {
  getProofOptions,
  getProofQueryKey,
  useDeleteProof,
  useSubmitProof,
  useValidateProof,
} from 'generated/api-client/@tanstack/react-query.gen';
import type { ReviewProofResponse } from 'generated/api-client/types.gen';
import { useIsUserAdmin } from 'hooks/use-is-user-admin/use-is-user-admin';
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { ProofDocumentCard } from './ProofDocumentCard';
import { ProofDocumentSelector } from './ProofDocumentSelector';

export const PROOF_INTRO_TEXT =
  "Ajoutez le document justificatif de la prestation (facture de solde ou d'acompte)";

type Props = {
  reviewId: number;
  open: boolean;
  onClose: () => void;
  onProofChange?: () => void;
};

type ProofModalState =
  | { kind: 'empty' }
  | { kind: 'selected'; file: File }
  | { kind: 'uploaded'; document: ReviewProofResponse }
  | { kind: 'validated'; document: ReviewProofResponse };

export const ProofDocumentModal: FunctionComponent<Props> = ({
  reviewId,
  open,
  onClose,
  onProofChange,
}) => {
  const queryClient = useQueryClient();
  const isCurrentUserAdmin = useIsUserAdmin();
  const [selectedDocument, setSelectedDocument] = useState<File>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const proofQueryKey = getProofQueryKey({ path: { reviewId } });

  const { data, isLoading: isLoadingProof } = useQuery({
    ...getProofOptions({ path: { reviewId } }),
    enabled: open,
  });

  const { mutateAsync: uploadDocument, isPending: isUploading } =
    useSubmitProof({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: proofQueryKey });
        setSelectedDocument(undefined);
        onProofChange?.();
        ToastSuccess('Succès', 'Document justificatif ajouté');
      },
      onError: () => {
        ToastError('Erreur', "Impossible d'ajouter le document justificatif");
      },
    });

  const { mutateAsync: deleteUploadedDocument, isPending: isDeleting } =
    useDeleteProof({
      onSuccess: async () => {
        await queryClient.resetQueries({ queryKey: proofQueryKey });
        setIsPreviewOpen(false);
        onProofChange?.();
        ToastSuccess('Succès', 'Document justificatif supprimé');
      },
      onError: () => {
        ToastError(
          'Erreur',
          'Impossible de supprimer le document justificatif',
        );
      },
    });

  const { mutateAsync: validateUploadedDocument, isPending: isValidating } =
    useValidateProof({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: proofQueryKey });
        onProofChange?.();
        ToastSuccess('Succès', 'Document justificatif validé');
      },
      onError: () => {
        ToastError('Erreur', 'Impossible de valider le document justificatif');
      },
    });

  const isMutating = isUploading || isDeleting || isValidating;

  const state = useMemo<ProofModalState>(() => {
    const proof = data?.proof;

    if (proof) {
      return proof.status === 'validated'
        ? { kind: 'validated', document: proof }
        : { kind: 'uploaded', document: proof };
    }

    if (selectedDocument) {
      return { kind: 'selected', file: selectedDocument };
    }

    return { kind: 'empty' };
  }, [data, selectedDocument]);

  useEffect(() => {
    setSelectedDocument(undefined);
    setIsPreviewOpen(false);
  }, [reviewId, open]);

  const previewDocument =
    (state.kind === 'uploaded' || state.kind === 'validated') &&
    state.document.mimeType !== undefined
      ? {
          mimeType: state.document.mimeType,
          name: state.document.name,
          url: state.document.previewUrl,
        }
      : undefined;

  const canPreview = previewDocument !== undefined;

  const handleDownload = useCallback(
    (document: { name: string; downloadUrl: string }) => {
      const link = window.document.createElement('a');
      link.href = document.downloadUrl;
      link.download = document.name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    },
    [],
  );

  const renderContent = (): ReactNode => {
    switch (state.kind) {
      case 'empty':
      case 'selected':
        return (
          <>
            <p style={{ marginTop: 0, marginBottom: '20px', lineHeight: 1.5 }}>
              {PROOF_INTRO_TEXT}
              <br />
              Après vérification, celui-ci permettra d&apos;augmenter le niveau
              de confiance attribué à l&apos;avis.
            </p>
            <ProofDocumentSelector
              document={state.kind === 'selected' ? state.file : undefined}
              onDocumentChange={setSelectedDocument}
              disabled={isMutating}
            />
          </>
        );
      case 'uploaded':
      case 'validated': {
        const canValidate = state.kind === 'uploaded' && isCurrentUserAdmin;
        return (
          <ProofDocumentCard name={state.document.name} status={state.kind}>
            <Button
              basic
              color="blue"
              type="button"
              disabled={!canPreview || isMutating}
              onClick={(): void => setIsPreviewOpen(true)}
            >
              <Icon name="eye" />
              Visualiser
            </Button>
            <Button
              basic
              color="blue"
              type="button"
              disabled={isMutating}
              onClick={() => handleDownload(state.document)}
            >
              <Icon name="download" />
              Télécharger
            </Button>
            {canValidate ? (
              <Button
                basic
                color="green"
                type="button"
                loading={isValidating}
                disabled={isMutating}
                onClick={() => {
                  void validateUploadedDocument({ path: { reviewId } });
                }}
              >
                <Icon name="check" />
                Valider
              </Button>
            ) : null}
            <Button
              basic
              color="red"
              type="button"
              loading={isDeleting}
              disabled={isMutating}
              onClick={() => {
                void deleteUploadedDocument({ path: { reviewId } });
              }}
            >
              <Icon name="trash alternate outline" />
              Supprimer
            </Button>
          </ProofDocumentCard>
        );
      }
    }
  };

  const canUpload = state.kind === 'empty' || state.kind === 'selected';
  const fileToUpload = state.kind === 'selected' ? state.file : undefined;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        closeIcon
      >
        <Modal.Header>Preuve de prestation</Modal.Header>
        <Modal.Content>
          {isLoadingProof ? 'Chargement...' : renderContent()}
        </Modal.Content>

        <Modal.Actions>
          <Button type="button" onClick={onClose}>
            Fermer
          </Button>
          {canUpload ? (
            <Button
              type="button"
              primary
              loading={isUploading}
              disabled={!fileToUpload || isMutating}
              onClick={() => {
                if (!fileToUpload) {
                  return;
                }
                void uploadDocument({
                  body: { proof: fileToUpload },
                  path: { reviewId },
                });
              }}
            >
              Ajouter
            </Button>
          ) : null}
        </Modal.Actions>
      </Modal>

      <DocumentPreviewModal
        open={isPreviewOpen}
        onClose={(): void => setIsPreviewOpen(false)}
        document={previewDocument}
      />
    </>
  );
};
